import { useEffect, useState, useCallback } from 'react';
import { api, readImpMarker, clearImpersonation, type ImpMarker } from '../lib/api';

/// <summary>
/// Persistent banner shown across the whole tenant app while a support-impersonation session is
/// active. Undismissable by design — the only way to remove it is Exit, which ends the session.
/// Shows the school being viewed and a live countdown; on expiry it exits automatically (the
/// token has expired server-side anyway, and the read-only guard was in force throughout).
/// </summary>
export function ImpersonationBanner() {
  const [marker] = useState<ImpMarker | null>(() => readImpMarker());
  const [remaining, setRemaining] = useState<number | null>(null);

  const exit = useCallback(async () => {
    try { await api.post('/api/platform/impersonate/end', {}); } catch { /* best-effort */ }
    clearImpersonation();
    window.location.href = '/?platform';
  }, []);

  useEffect(() => {
    if (!marker) return;
    const tick = () => setRemaining(Math.max(0, Math.floor((marker.expiresAt - Date.now()) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [marker]);

  useEffect(() => {
    if (marker && remaining === 0) exit(); // auto-exit once the countdown reaches zero
  }, [remaining, marker, exit]);

  if (!marker) return null;

  const secs = remaining ?? 0;
  const mm = String(Math.floor(secs / 60)).padStart(2, '0');
  const ss = String(secs % 60).padStart(2, '0');

  return (
    <div style={{
      position: 'sticky', top: 0, zIndex: 100,
      background: 'var(--danger, #C1272D)', color: '#fff',
      display: 'flex', alignItems: 'center', gap: 12, padding: '8px 16px', fontSize: 13,
    }}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <path d="M12 9v4M12 17h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      </svg>
      <span>
        <strong>Support session</strong> — viewing <strong>{marker.tenantName}</strong> as read-only support.
        This school is notified of your access.
      </span>
      <span className="mono" style={{ marginLeft: 'auto', opacity: 0.9 }}>ends in {mm}:{ss}</span>
      <button onClick={exit} style={{
        background: 'rgba(255,255,255,.2)', color: '#fff', border: '1px solid rgba(255,255,255,.4)',
        borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 600,
      }}>Exit</button>
    </div>
  );
}
